/**
 * Design token injection for shadow-DOM-based widgets.
 *
 * Copies CSS variables, stylesheets, and inline styles into a shadow root so
 * that widgets render with the correct visual tokens regardless of isolation.
 *
 * Two source paths are supported:
 * - **Embed path** — resources stored in `window.__insidedEmbed.headResources`
 *   by the resource-loader (external host pages).
 * - **Full-page path** — resources already present in `document.head` from
 *   server-rendered templates (community platform pages).
 */

/** Whitelisted font CDN hostnames whose stylesheets need document-level @font-face registration. */
const FONT_HOSTS = ['fonts.googleapis.com', 'fonts.gstatic.com']

function isFontStylesheet(href: string): boolean {
  try {
    return FONT_HOSTS.includes(new URL(href).hostname)
  } catch {
    return false
  }
}

/**
 * Injects design tokens (CSS variables, stylesheets, inline styles) into a
 * shadow root. Automatically selects the correct source based on context:
 * embed global resources when available, otherwise document.head.
 */
export function injectDesignTokens(shadowRoot: ShadowRoot): void {
  injectHeadResources(shadowRoot)

  if (!(window as any).__insidedEmbed?.headResources) {
    injectDesignTokensFromDocument(shadowRoot)
  }
}

interface HeadStylesheet {
  href: string
  attributes?: Record<string, string>
}
interface HeadStyle {
  id?: string
  content: string
}
interface HeadResources {
  stylesheets?: HeadStylesheet[]
  styles?: HeadStyle[]
}

function createStylesheetLink(href: string, attributes?: Record<string, string>): HTMLLinkElement {
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  if (attributes) {
    for (const [key, value] of Object.entries(attributes)) {
      link.setAttribute(key, value)
    }
  }
  return link
}

function injectStylesheetIntoShadow(shadowRoot: ShadowRoot, { href, attributes }: HeadStylesheet): void {
  if (shadowRoot.querySelector(`link[href="${href}"]`)) return
  shadowRoot.prepend(createStylesheetLink(href, attributes))
}

/**
 * External font stylesheets must also be registered on document.head because
 * @font-face rules loaded exclusively inside shadow DOM are not reliably
 * applied globally across all browsers.
 */
function registerFontStylesheetGlobally({ href, attributes }: HeadStylesheet): void {
  if (!isFontStylesheet(href)) return
  if (document.head.querySelector(`link[href="${href}"]`)) return
  document.head.appendChild(createStylesheetLink(href, attributes))
}

function injectInlineStyle(shadowRoot: ShadowRoot, { id, content }: HeadStyle): void {
  if (id && shadowRoot.getElementById(id)) return
  const style = document.createElement('style')
  if (id) style.id = id
  style.textContent = id === 'css-variables' ? content.replace(/html\s*\{/, ':host {') : content
  shadowRoot.prepend(style)
}

/**
 * Embed path: the resource-loader stores parsed head resources (stylesheets, styles)
 * in window.__insidedEmbed.headResources. Inject them into the shadow root so the
 * widget renders with the correct fonts, CSS variables and custom styles.
 */
function injectHeadResources(shadowRoot: ShadowRoot): void {
  const res = (window as any).__insidedEmbed?.headResources as HeadResources | undefined
  if (!res) return

  for (const stylesheet of res.stylesheets ?? []) {
    injectStylesheetIntoShadow(shadowRoot, stylesheet)
    registerFontStylesheetGlobally(stylesheet)
  }

  for (const style of res.styles ?? []) {
    injectInlineStyle(shadowRoot, style)
  }
}

/**
 * Full community page fallback: css-variables and font links are in document.head
 * from server-rendered templates. Copy them into the shadow root if not already present.
 */
function injectDesignTokensFromDocument(shadowRoot: ShadowRoot): void {
  if (!shadowRoot.getElementById('css-variables')) {
    const source = document.getElementById('css-variables')
    if (source instanceof HTMLStyleElement && source.textContent) {
      const style = document.createElement('style')
      style.id = 'css-variables'
      style.textContent = source.textContent.replace(/html\s*\{/, ':host {')
      shadowRoot.prepend(style)
    }
  }

  const links = document.head.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')
  for (const link of links) {
    const href = link.getAttribute('href')
    if (!href) continue
    if (shadowRoot.querySelector(`link[href="${href}"]`)) continue
    shadowRoot.prepend(link.cloneNode(true))
  }
}
